import { bannerSceneUrl } from "../shops/shopsArt";
import type { OfferView, PageView } from "@highmarch/app";
import { BalanceData, OfferItem, offerOf, priceCents } from "@highmarch/domain";
import { BannerTheme, checkHtml, emptyHtml, flagHtml, heroHtml, kickerHtml, secHtml, tagHtml } from "../components";
import { icon } from "../iconsKit";
import { uiUrl } from "../portraits";
import {
  ItemTile, TileNames, blockedText, countdownText, dailySplit, gemsOf, itemTile, itemTiles, needsChoice, pageArt, pageBlurb, priceText,
  remainingText, resetLine, trackFill, trackStops, wasText,
} from "./dealsModel";
import { PriceSize, countdownHtml, gemPackArt, gemPackSlotHtml, plusSlotHtml, priceBtnHtml, valueBurstHtml, wellOf, wellsOf, worthTagHtml } from "./dealsTiles";
import { CardsCtx, cardsBodyHtml } from "./cardsPage";

/**
 * One Deals page as markup, in the game's "Keep & Plate" language (design system v2, ui/components.ts) with Kingshot's
 * density: the painted hero in its navy plate (the page's character, a real countdown, the blurb, the Daily "Change"),
 * the Weekly token track, then the offers as cream cards — a title flag, the items in wells, the one value burst, the
 * gold price with its VIP XP bubble, "Remaining" and the Odds tag. The page's colour lives only in its hero art.
 * Every number and word comes from the app's views through dealsModel.ts.
 */

export interface PageCtx {
  readonly balance: BalanceData;
  readonly level: number;
  readonly now: number;
  readonly prices: ReadonlyMap<string, string>;
  readonly names: TileNames;
  /** The web's test store: its note in the footer. */
  readonly sandbox: boolean;
  /** The open page's title (a one-off pack named like its page does not repeat it). */
  readonly pageTitle: string;
  /** Gem packs (the Gems page), with the value and VIP points their price gives. */
  readonly gemPacks: readonly { id: string; name: string; gems: number; referencePrice: string; value: { percent: number; show: boolean }; vipPoints: number; first: boolean; tag?: "popular" | "best" }[];
  /** The Cards and Fund pages' views (MP-01..03, cardsPage.ts). */
  readonly cards?: CardsCtx;
  /** A ladder page's tier on show (shop redesign: one pack at a time); undefined = the first one still for sale. */
  readonly ladderAt?: number;
}

/**
 * Shop redesign (owner 2026-10-19): these pages are price ladders — $4.99 → $9.99 → $19.99 → $49.99 → $99.99 — shown one
 * pack at a time; buying a tier brings the next one up, the arrows look back and ahead.
 */
export const LADDER_PAGES: ReadonlySet<string> = new Set(["masters", "custom", "armoury", "treasury", "builders"]);

/** The tier a ladder page opens on: the first one still for sale, else the last. */
export function ladderStart(offers: readonly OfferView[]): number {
  const i = offers.findIndex((o) => o.blocked !== "sold out");
  return i >= 0 ? i : Math.max(0, offers.length - 1);
}

const esc = (s: string): string => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" })[c]!);

/** Where the hero art's focal point sits: Captain Bram presenting the page's wares in the right half
 * (tools/art/kit/heroes_bpy.py frames him at 70 % across, his helmet inside the band this crop keeps). */
const HERO_FOCUS = "70% 52%";
/** A one-off pack's items show in full-size wells up to this many; more drop to the small wells so one row holds them. */
const HERO_MD_WELLS = 4;
/** The Daily Deals tiers' marks. */

export function pageHtml(p: PageView, c: PageCtx): string {
  return `<section class="dpage" data-page="${p.page.id}">${pageHeroHtml(p, c)}${p.track ? trackHtml(p, c) : ""}<div class="dbody">${bodyHtml(p, c)}</div>${footHtml(p, c)}</section>`;
}

// ------------------------------------------------------------------ hero

/** The countdown's words by the page's rotation. */
function timerLabel(p: PageView): string {
  switch (p.page.rotation) {
    case "daily": return "New deals in";
    case "weekly": return "Resets in";
    default: return "Ends in";
  }
}

/** The hero line over a one-time page's follow-up pack (MP-04). */
const FOLLOW_UP_BLURB = "The next step for a rising Commander.";

function pageHeroHtml(p: PageView, c: PageCtx): string {
  const cd = countdownText(p.endsAt, c.now);
  const timer = cd !== null && p.endsAt !== null ? countdownHtml(p.endsAt, cd, c.now, timerLabel(p)) : "";
  const kicker = p.theme ? `Today: ${esc(p.theme.name)}` : p.page.rotation === "once" ? "Once only" : "";
  // A one-time page showing only its follow-up offer (MP-04: Field Commander after First Command) is titled by it.
  const follow = p.page.rotation === "once" && p.offers.length === 1 && p.offers[0].name !== p.page.title ? p.offers[0] : null;
  const title = follow ? follow.name : p.page.title, blurb = follow ? FOLLOW_UP_BLURB : pageBlurb(p.page);
  void kicker; void blurb;   // the bright banner carries no taglines (shop redesign 2026-10-19)
  const b = PAGE_BANNER[p.page.id] ?? { theme: "sky", cast: "pointing" };
  return heroHtml({
    art: uiUrl(pageArt(p)), title: esc(title), timer, side: p.theme ? changeHtml(p) : "", focus: HERO_FOCUS, className: "dhero",
    bright: { theme: b.theme, cast: uiUrl(`captain/bram_${b.cast}@3x.png`), prop: uiUrl(p.page.id === "builders" ? "icons/event_builders@3x.png" : `icons/deals_${p.page.id}.png`), scene: bannerSceneUrl(p.page.id) },
  });
}

/** Each page's daylight banner: its backdrop and Captain Bram's pose (the page's own icon is the prop beside him). */
const PAGE_BANNER: Readonly<Record<string, { theme: BannerTheme; cast: "pointing" | "thumbsup" }>> = {
  starter: { theme: "sky", cast: "pointing" }, welcome: { theme: "sky", cast: "thumbsup" }, daily: { theme: "sky", cast: "pointing" },
  weekly: { theme: "gold", cast: "thumbsup" }, custom: { theme: "royal", cast: "pointing" }, masters: { theme: "sky", cast: "thumbsup" },
  commander: { theme: "gold", cast: "pointing" }, chapter: { theme: "camp", cast: "thumbsup" }, armoury: { theme: "camp", cast: "pointing" },
  treasury: { theme: "gold", cast: "thumbsup" }, cards: { theme: "royal", cast: "thumbsup" }, fund: { theme: "gold", cast: "pointing" },
  gems: { theme: "royal", cast: "pointing" }, builders: { theme: "gold", cast: "pointing" }, vip: { theme: "royal", cast: "thumbsup" },
};

/** Daily Deals: "Change" today's theme (dimmed with its reason once today's changes are used or a tier bought). */
function changeHtml(p: PageView): string {
  const left = p.theme!.changesLeft;
  return `<button type="button" class="btn blue dchange${left > 0 ? "" : " dis"}" data-left="${left}">${icon("rotate", 18, "#fff", 2.6)}<span>Change</span></button>`;
}

/**
 * The Weekly token track: the tokens held, each milestone's free reward in a well over the bar, the bar's gold fill
 * and a pip per milestone. Evenly spaced columns; the bar runs from the token count (0) to the last column's centre.
 */
function trackHtml(p: PageView, c: PageCtx): string {
  const t = p.track!;
  const stops = trackStops(t);
  const n = stops.length;
  const cols = stops.map((s) => {
    const tile = s.items[0] ? itemTile(c.balance, s.items[0], c.level) : null;
    const names = s.items.map((i) => itemTile(c.balance, i, c.level).name).join(", ");
    const more = s.items.length > 1 ? `<i class="tmore">+${s.items.length - 1}</i>` : "";
    return `<button type="button" class="tstop s-${s.state}" data-tokens="${s.tokens}" aria-label="${s.tokens} tokens: ${esc(names)}${s.state === "claimed" ? " (claimed)" : s.state === "ready" ? " (claim now)" : ""}">`
      + `<span class="trew">${tile ? wellOf(tile, "sm", "span", s.state === "claimed" ? "dim" : "") : ""}${more}${s.state === "claimed" ? `${checkHtml("tcheck")}` : ""}</span>`
      + `<b class="tn">${s.tokens}</b></button>`;
  }).join("");
  const pips = stops.map((s, i) => `<i class="pip${s.state !== "locked" ? " on" : ""}" style="--x:${((i + 0.5) / n).toFixed(4)}"></i>`).join("");
  return `<section class="k-card plate dtrack" style="--n:${n}"><div class="thead"><img decoding="async" src="${uiUrl("icons/weekly_token.png")}" alt="">`
    + `<b class="disp">${t.tokens}</b><span class="tlbl">Weekly Tokens</span></div>`
    + `<div class="tstops">${cols}</div><div class="bar tbar"><div class="fill gold" style="width:${(barFill(trackFill(t), n) * 100).toFixed(2)}%"></div>${pips}</div></section>`;
}

/**
 * The model's fill (milestones evenly spaced, stop i at (i+1)/n) on this bar, where stop i sits at its column's centre
 * ((i+0.5)/n) and 0 tokens at the left edge: the first half-column covers the way to the first milestone.
 */
export function barFill(f: number, n: number): number {
  if (n <= 0) return 0;
  const k = Math.max(0, Math.min(1, f)) * n;
  return k < 1 ? (k * 0.5) / n : (k - 0.5) / n;
}

// ------------------------------------------------------------------ body

function bodyHtml(p: PageView, c: PageCtx): string {
  if (p.page.gemPacks) return gemsHtml(c);
  if (p.page.daily) return dailyHtml(p, c);
  if (c.cards && (p.page.id === "cards" || p.page.id === "fund")) return cardsBodyHtml(p, c, c.cards);
  if (p.offers.length === 0) return emptyHtml(uiUrl(`icons/deals_${p.page.id}.png`), "Sold out, Commander. Fresh stock at the next reset.");
  const hero = p.page.rotation === "once";
  const card = (o: OfferView) => (o.picks.length > 0 ? customHtml(o, c) : o.prizes.length > 0 ? mastersHtml(o, c) : hero ? heroOfferHtml(o, c) : packHtml(o, c));
  if (LADDER_PAGES.has(p.page.id) && p.offers.length > 1) return ladderHtml(p, c, card);
  return p.offers.map(card).join("");
}

/** A ladder page: the tier on show (its name between the arrows), its card, and the ladder of prices under it. */
function ladderHtml(p: PageView, c: PageCtx, card: (o: OfferView) => string): string {
  const n = p.offers.length, i = Math.max(0, Math.min(n - 1, c.ladderAt ?? ladderStart(p.offers))), o = p.offers[i];
  const start = ladderStart(p.offers);
  const chips = p.offers.map((x, k) => `<span class="dlchip${x.blocked === "sold out" ? " done" : k === start ? " now" : ""}${k === i ? " on" : ""}">${esc(priceText(x.price, c.prices))}</span>`).join("");
  return `<div class="dladder" data-at="${i}"><div class="dlnav"><button type="button" class="dlstep prev${i === 0 ? " off" : ""}" data-step="-1" aria-label="Previous pack">${icon("chev", 16)}</button>`
    + `<b class="disp">${esc(o.name)}</b><button type="button" class="dlstep next${i === n - 1 ? " off" : ""}" data-step="1" aria-label="Next pack">${icon("chev", 16)}</button></div>`
    + `${card(o)}<div class="dlchips">${chips}</div></div>`;
}

/** `inColumn`: the price sits in a card's pay column under its value burst, so the VIP XP bubble takes the left shoulder. */
function priceFor(o: OfferView, c: PageCtx, size: PriceSize = "card", was: string | null = null, inColumn = true): string {
  const soldOut = o.blocked === "sold out";
  return priceBtnHtml({ offerId: o.offerId, label: priceText(o.price, c.prices), vipPoints: o.vipPoints, blocked: o.blocked ? blockedText(o.blocked) : "", was, soldOut, choice: needsChoice(o.blocked), bubbleLeft: inColumn }, size);
}

/** Odds before paying (Apple 3.1.1): the INFO tag in a 44 px button, on every offer that holds a random item. */
function oddsHtml(o: OfferView): string {
  return "";   // no ODDS button (owner 2026-10-21): a tap on the chest shows it in focus with its odds
}

/** The one value marker: the burst on a one-off ("percent" page), or the quiet "Worth N gems" tag on a recurring deal (MP-09). */
function burst(o: OfferView): string {
  if (o.blocked === "sold out") return "";
  // Shop redesign (owner 2026-10-19, round-3 mockups): every pack wears its honest value as the starburst on its top
  // edge (MP-09's quiet "Worth N gems" tag stays for an offer with nothing to compare: a card or the pass).
  if (o.valuePercent >= VALUE_FROM_PERCENT) return valueBurstHtml(o.valuePercent);
  return o.worthGems !== null ? worthTagHtml(o.worthGems) : "";
}
/** The starburst shows from here (the gem packs' own 100 % stays plain). */
const VALUE_FROM_PERCENT = 110;
const soldClass = (o: OfferView): string => (o.blocked === "sold out" ? " soldout" : "");

/** The price column: the price, "Remaining: n", the Odds tag. */
function payHtml(o: OfferView, c: PageCtx): string {
  const rem = remainingText(o);
  return `<div class="dpay">${priceFor(o, c)}${rem ? `<small class="rem">${rem}</small>` : ""}${oddsHtml(o)}</div>`;
}

/** A Weekly Deal pack's token, listed among the items as Kingshot does. */
function tokenWell(o: OfferView, c: PageCtx): string {
  const n = offerOf(c.balance.offers, o.offerId)?.weeklyTokens ?? 0;
  if (n <= 0) return "";
  const t: ItemTile = { kind: "gems", icon: "ui:icons/weekly_token.png", top: "", count: n > 1 ? `×${n}` : "", tone: "gem", name: `${n} Weekly Token${n > 1 ? "s" : ""}`, detail: "Fills this week's track of free rewards", random: false, needsSlot: false };
  return wellOf(t, "sm", "button", "token");
}

/** A pack card (Weekly Deal, Armoury, Treasury): flag, wells, the price column. */
function packHtml(o: OfferView, c: PageCtx): string {
  const tiles = itemTiles(c.balance, o.items, c.level, o.gearSlots, c.names);
  return `<article class="k-card doffer pack${soldClass(o)}" data-offer="${o.offerId}">${flagHtml(esc(o.name))}${burst(o)}
    <div class="drow"><div class="dwells">${wellsOf(tiles)}${tokenWell(o, c)}</div>${payHtml(o, c)}</div></article>`;
}

/** A one-off hero pack (First Command, Commander's, a Chapter pack): the headline gems, the wells, the big price. */
function heroOfferHtml(o: OfferView, c: PageCtx): string {
  // a builder offer (docs/SHOP.md §18) leads with the builder it hires; its gems join the extras' row
  const builder = o.builder ?? 0;
  const gems = builder > 0 ? 0 : gemsOf(o.items);
  const tiles = itemTiles(c.balance, builder > 0 ? o.items : o.items.filter((i) => i.kind !== "gems"), c.level, o.gearSlots, c.names);
  const rem = remainingText(o);
  return `<article class="k-card hl doffer hero${builder > 0 ? " builder" : ""}${soldClass(o)}" data-offer="${o.offerId}">${o.name !== c.pageTitle && builder === 0 ? flagHtml(esc(o.name)) : ""}${burst(o)}
    ${builder > 0 ? `<div class="hgems hbuilder"><img decoding="async" src="${uiUrl("icons/event_builders@3x.png")}" alt=""><b class="disp">Builder ${builder}</b></div><p class="hbline">Run ${builder} upgrades at once</p>` : ""}
    ${gems > 0 ? `<div class="hgems"><img decoding="async" src="${uiUrl("icons/gem_pack_3@3x.png")}" alt=""><b class="disp">${gems.toLocaleString("en-US")}</b><small>gems</small></div>` : ""}
    <div class="dwells center">${wellsOf(tiles, tiles.length <= HERO_MD_WELLS ? "md" : "sm")}</div>
    <div class="hmeta">${rem ? `<small class="rem">${rem}</small>` : ""}${oddsHtml(o)}</div>${priceFor(o, c, "main", null, false)}</article>`;
}

function gearPicksBefore(items: readonly OfferItem[]): number {
  return items.reduce((n, i) => n + (i.kind === "gear" && i.slot === "pick" ? i.count : 0), 0);
}

/** Custom Chest: three pick slots ("+" until filled) over the fixed items, the price column (blue "Fill" first). */
function customHtml(o: OfferView, c: PageCtx): string {
  const tier = o.offerId.split(".")[1] ?? "common";
  const fixedItems = offerOf(c.balance.offers, o.offerId)?.items ?? [];
  // A gear choice needs its slot: its index among the offer's gear picks is the fixed items' then the earlier picks'.
  let g = gearPicksBefore(fixedItems);
  const picks = o.picks.map((choice, slot) => {
    if (choice < 0) return plusSlotHtml(slot);
    const item = o.choices[choice];
    const gi = item.kind === "gear" && item.slot === "pick" ? g : -1;
    if (gi >= 0) g += item.count;
    const t = itemTile(c.balance, item, c.level, gi >= 0 ? (o.gearSlots[gi] ?? -1) : -1, c.names);
    return wellOf(t, "sm", "button", "picked", `data-slot="${slot}"${gi >= 0 ? ` data-gear="${gi}"` : ""}`);
  }).join("");
  const filled = o.picks.filter((p) => p >= 0).length;
  return `<article class="k-card doffer custom tier-${tier}${soldClass(o)}" data-offer="${o.offerId}">${flagHtml(esc(o.name))}${burst(o)}
    <div class="drow"><div class="dgrid">${kickerHtml("Your picks", `${filled} of ${o.picks.length}`)}<div class="picks">${picks}</div>
    ${kickerHtml("Always inside")}<div class="dwells">${wellsOf(itemTiles(c.balance, fixedItems, c.level, [], c.names))}</div></div>${payHtml(o, c)}</div></article>`;
}

/** Master's Collection: "Choose your prize" (one of three), the fixed items, the price column. */
function mastersHtml(o: OfferView, c: PageCtx): string {
  const fixed = offerOf(c.balance.offers, o.offerId)?.items ?? [];
  const gearBefore = gearPicksBefore(fixed);
  const prizes = o.prizes.map((item, i) => {
    const on = o.prize === i;
    const t = itemTile(c.balance, item, c.level, on && item.kind === "gear" && item.slot === "pick" ? (o.gearSlots[gearBefore] ?? -1) : -1, c.names);
    return `<button type="button" class="prize${on ? " on" : ""}${o.prize >= 0 && !on ? " off" : ""}" data-prize="${i}" aria-pressed="${on}">${wellOf(t, "md", "span")}<small>${esc(t.name)}</small>${on ? `${checkHtml("dchk")}` : ""}</button>`;
  }).join("");
  return `<article class="k-card doffer masters${soldClass(o)}" data-offer="${o.offerId}">${flagHtml(esc(o.name))}${burst(o)}
    ${kickerHtml("Choose your prize", o.prize < 0 ? "one of three" : "tap another to change")}<div class="prizes">${prizes}</div>
    <div class="drow"><div class="dwells">${wellsOf(itemTiles(c.balance, fixed, c.level, o.gearSlots, c.names))}</div>${payHtml(o, c)}</div></article>`;
}

/**
 * Daily Deals (owner 2026-10-21, round 4: Kingshot's compact rows): Buy All first, showing everything the three deals
 * hold together and its struck-through sum; then each deal as one compact card: its gems and value on the top line,
 * the items and the price on the next. No "Deal I/II/III" flags.
 */
function dailyHtml(p: PageView, c: PageCtx): string {
  const { tiers, buyAll } = dailySplit(p);
  const tierPrices = tiers.map((t) => t.price);
  const gemsLine = (n: number) => (n > 0 ? `<div class="tgems"><img decoding="async" src="${uiUrl("icons/gem@2x.png")}" alt=""><b class="disp">${n.toLocaleString("en-US")}</b><small>gems</small></div>` : "");
  const all = buyAll ? (() => {
    const items = buyAll.items.length > 0 ? buyAll.items : tiers.flatMap((t) => t.items);
    const tiles = itemTiles(c.balance, items.filter((it) => it.kind !== "gems"), c.level, buyAll.gearSlots, c.names);
    return `<article class="k-card hl doffer dall tcompact${soldClass(buyAll)}" data-offer="${buyAll.offerId}">${burst(buyAll)}
      <div class="trow1"><b class="disp dalltitle">Buy All</b>${gemsLine(gemsOf(items))}</div>
      <div class="drow"><div class="dwells">${wellsOf(tiles)}</div><div class="dpay">${priceFor(buyAll, c, "card", wasText(buyAll.wasCents, tierPrices, c.prices))}</div></div>
      ${buyAll.blocked ? `<p class="dallnote">Only before any deal is bought today</p>` : ""}</article>`;
  })() : "";
  const rows = tiers.map((o) => {
    const tiles = itemTiles(c.balance, o.items.filter((it) => it.kind !== "gems"), c.level, o.gearSlots, c.names);
    return `<article class="k-card doffer tier tcompact${o.blocked ? " soldout" : ""}" data-offer="${o.offerId}">${burst(o)}
      <div class="trow1">${gemsLine(gemsOf(o.items))}</div>
      <div class="drow"><div class="dwells">${wellsOf(tiles)}</div><div class="dpay">${priceFor(o, c)}</div></div></article>`;
  }).join("");
  return `${all}${secHtml("Today's deals")}${rows}`;
}

/** The gem packs: the pile by size in a navy well, the gems, the honest value (from 110 %), the store's price, VIP XP. */
function gemsHtml(c: PageCtx): string {
  if (c.gemPacks.length === 0) return emptyHtml(uiUrl("icons/gem_pack_3@3x.png"), "Gem packs come with the iPhone app.");
  const slots = c.gemPacks.map((g, i) => gemPackSlotHtml({
    id: g.id, name: g.name, gems: g.gems, price: c.prices.get(g.id) ?? g.referencePrice, value: g.value, vipPoints: g.vipPoints,
    art: gemPackArt(i), first: g.first, tag: g.tag,
  })).join("");
  return `<div class="k-grid3 dgems">${slots}</div>`;
}

// owner 2026-10-19: no footnotes under a page (reset rules, "Test build"): the timer chip says when it resets.
function footHtml(_p: PageView, _c: PageCtx): string {
  return "";
}

/** Gem pack VIP points and value from the reference price (the same rules as a Deals offer). */
export function gemPackCtx(packs: readonly { id: string; name: string; gems: number; referencePrice: string; tag?: "popular" | "best" }[],
  value: (items: readonly OfferItem[], cents: number) => { percent: number; show: boolean }, vip: (cents: number) => number, first: (id: string) => boolean = () => false): PageCtx["gemPacks"] {
  return packs.map((p) => {
    let cents = 0;
    try { cents = priceCents(p.referencePrice); } catch { cents = 0; }
    return { ...p, value: cents > 0 ? value([{ kind: "gems", amount: p.gems, count: 1 }], cents) : { percent: 0, show: false }, vipPoints: cents > 0 ? vip(cents) : 0, first: first(p.id) };
  });
}
